import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Column from '@amzn/meridian/column'
import Loader from '@amzn/meridian/loader'
import Text from '@amzn/meridian/text'
import Box from '@amzn/meridian/box'
import Row from '@amzn/meridian/row'
import Button from '@amzn/meridian/button'
import { useBundle } from '@amzn/react-arb-tools'
import { getEffectiveStoreId, getStoreList } from '@atredux/selectors/storeSelector'
import { setNotificationCount } from '@atredux/slices/notificationSlice'
import { BUNDLE_NAME, NO_CURRENT_STORE } from '@src/constants'
import { t } from '@src/translationHelper'
import { ActionCenterAPIService } from './services/ActionCenterAPIService'
import { ActionCenterTab, ActionCenterView, IActionCenterNotification, IStoreFilterOption } from './types'
import { CLEAR_FILTER, EMPTY_ALL_CAUGHT_UP, LOAD_ERROR, LOADING, NEEDS_IMMEDIATE_ATTENTION, STORE_AGNOSTIC_STORE_ID, STORE_SELECTION_SUMMARY } from './constants'
import { groupByCategory, countUnread, needsImmediateAttention, sortNotifications } from './utils'
import { NotificationCard } from './components/NotificationCard'
import { CategoryAccordion, useCategoryLabel } from './components/CategoryAccordion'
import { NotificationDetailView } from './components/NotificationDetailView'
import { BackHeader, SectionHeader } from './components/Headers'
import { StoreLoadErrorNotice, StoreTabs, TruncatedNotice, ViewOnlyNotice } from './components/StoreTabs'
import { StoreFilterSheet } from './components/StoreFilterSheet'
import './ActionCenterPage.scss'

/**
 * Action Center (golden_gate_in_app_notification_page_v2).
 *
 * Two reads through the Gateway ListNotifications proxy: the caller's unread inbox across every
 * store (the "All stores" tab and the bell count) and the current store's full history, read and
 * unread (the "Current store" tab). Each tab is then sliced client-side into a severity-ranked
 * "Needs immediate attention" section (CRITICAL + HIGH) and a per-category accordion, with unread
 * items ahead of read ones. A card opens a detail view where an unread notification can be
 * acknowledged (marked READ).
 *
 * Counting rule: every number on the page counts unread items only. The tab badges, the
 * "Needs immediate attention" badge and the category rows all use `countUnread`, so on the
 * Current store tab the badge always equals the section badge plus the sum of the category rows,
 * and matches the unread "All stores" badge for the same store. Acknowledged items stay listed
 * on the Current store tab as history, without the unread dot and without being counted.
 *
 * The store read is separate because the service keys the two reads on the presence of
 * `storeId`, and because the Gateway only translates store ids to the platform's ids when the
 * request carries one: only the store-scoped items are guaranteed to compare equal to the
 * current store.
 *
 * "Current store" is the store the user is operating in: the scanned store when there is one,
 * otherwise the store chosen in the header (the same resolution the legacy Solve gate used).
 */
export const ActionCenterPage: React.FC = () => {
    // The selector already drops the NO_CURRENT_STORE sentinel from the scanned store; the guard
    // keeps it from ever reaching the store read should a future writer put it in _selectedStore.
    const effectiveStoreId = useSelector(getEffectiveStoreId)
    const currentStoreId = effectiveStoreId && effectiveStoreId !== NO_CURRENT_STORE ? effectiveStoreId : undefined
    const hasCurrentStore = !!currentStoreId
    const dispatch = useDispatch()
    const [bundle] = useBundle(BUNDLE_NAME)
    const categoryLabel = useCategoryLabel()
    const reduxStoreList = useSelector(getStoreList)

    const [allUnread, setAllUnread] = useState<IActionCenterNotification[] | null>(null)
    const [storeHistory, setStoreHistory] = useState<IActionCenterNotification[]>([])
    // Per tab: the read hit the page cap, so the accordion holds only the newest items.
    const [allTruncated, setAllTruncated] = useState(false)
    const [storeTruncated, setStoreTruncated] = useState(false)
    const [isLoading, setLoading] = useState(false)
    // The unread read failed: nothing to show, whole page is an error.
    const [loadFailed, setLoadFailed] = useState(false)
    // Only the store read failed: "All stores" (and the bell) still work, the store tab says so.
    const [storeLoadFailed, setStoreLoadFailed] = useState(false)
    const [activeTab, setActiveTab] = useState<ActionCenterTab>(hasCurrentStore ? 'current-store' : 'all-stores')
    const [view, setView] = useState<ActionCenterView>({ kind: 'list' })

    // Store-scope filter for the "All stores" tab. Empty = no filter ("All stores").
    const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([])
    const [isFilterOpen, setFilterOpen] = useState(false)

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        setLoadFailed(false)
        setStoreLoadFailed(false)
        // The "All stores" feed is built from per-store UNREAD reads (listStoreUnreadNotifications
        // sends the storeId, so the Gateway rewrites each item's storeId to the platform id, AND
        // status=UNREAD, so the server filters to unread). Every item then carries the platform
        // store id, which is what lets the filter list the full authorized store set and scope
        // exactly to a chosen store (P519064327) -- the plain unread read returns producer-written
        // ids that do not reconcile to the store list. Filtering unread SERVER-side (not after the
        // page cap on the client) means a store's read history cannot crowd its unread items past
        // the cap, and `truncated` honestly reflects unread truncation. Store-agnostic (GLOBAL)
        // items have no owning store, so they never come back from a per-store read; they are
        // picked out of the plain unread read and merged in, staying visible in "All stores" while
        // never being a filter row (they scope out under any store).
        //
        // Resilient fan-out: the per-store reads and the GLOBAL read are settled independently, so
        // one store's failure degrades to "that store's items are missing" rather than failing the
        // whole inbox. Only when EVERY read fails does the feed reject (-> whole-page error). Cost:
        // one read per authorized store plus the unread read. Until the store list has loaded, fall
        // back to the plain unread read so the tab is never empty.
        const unreadRead: Promise<{ items: IActionCenterNotification[], truncated: boolean }> = reduxStoreList.length > 0
            ? Promise.allSettled([
                ActionCenterAPIService.listUnreadNotifications(),
                ...reduxStoreList.map((s) => ActionCenterAPIService.listStoreUnreadNotifications(s.storeId)),
            ]).then((settled) => {
                if (settled.every((r) => r.status === 'rejected')) {
                    throw (settled[0] as PromiseRejectedResult).reason
                }
                const [inboxRes, ...perStoreRes] = settled
                const translated = perStoreRes
                    .flatMap((r) => (r.status === 'fulfilled' ? r.value.items : []))
                const seen = new Set(translated.map((n) => n.notificationId))
                const inboxItems = inboxRes.status === 'fulfilled' ? inboxRes.value.items : []
                const globals = inboxItems.filter((n) => n.storeId === STORE_AGNOSTIC_STORE_ID && !seen.has(n.notificationId))
                const feedTruncated = settled.some((r) => r.status === 'fulfilled' && r.value.truncated)
                return { items: [...translated, ...globals], truncated: feedTruncated }
            })
            : ActionCenterAPIService.listUnreadNotifications()
        // Settled, not fail-fast: the two reads are independent, so one failing must not throw
        // away the other's result.
        Promise.allSettled([
            unreadRead,
            currentStoreId ? ActionCenterAPIService.listStoreNotifications(currentStoreId) : Promise.resolve({ items: [], truncated: false }),
        ])
            .then(([unread, history]) => {
                if (cancelled) return
                if (unread.status === 'rejected') {
                    console.error('Failed to load notifications:', unread.reason)
                    setLoadFailed(true)
                    return
                }
                setAllUnread(unread.value.items)
                setAllTruncated(unread.value.truncated)
                if (history.status === 'rejected') {
                    console.error('Failed to load notifications for the current store:', history.reason)
                    setStoreHistory([])
                    setStoreTruncated(false)
                    setStoreLoadFailed(true)
                    return
                }
                setStoreHistory(history.value.items)
                setStoreTruncated(history.value.truncated)
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => { cancelled = true }
    }, [currentStoreId, reduxStoreList])

    // The bell shows the exact unread total whenever the feed is known (initial load and after each
    // acknowledge), overriding the single-page estimate it fetched on its own. Derived from state
    // rather than dispatched inside the state updaters, which React may run more than once.
    useEffect(() => {
        if (allUnread !== null) dispatch(setNotificationCount(allUnread.length))
    }, [allUnread, dispatch])

    // Leaving the store the user was in (or losing it) makes "Current store" meaningless.
    useEffect(() => {
        if (!hasCurrentStore && activeTab === 'current-store') setActiveTab('all-stores')
    }, [hasCurrentStore, activeTab])

    const all = useMemo(() => sortNotifications(allUnread ?? []), [allUnread])
    const current = useMemo(() => sortNotifications(storeHistory), [storeHistory])

    // Stores the "All stores" feed actually carries, named from the redux store list (id fallback).
    // These are the only meaningful filter targets: v2's "All stores" is a flat unread inbox, so a
    // store with no unread items has nothing to scope to. Two feed ids can resolve to the SAME
    // display name (store-id translation forms, casing, or same-named test stores); collapse those
    // into one row carrying every underlying id, so the sheet never shows duplicate rows the user
    // cannot tell apart (P519064327).
    // Filter rows are every store the user is authorized for (the same list the store switcher
    // shows), labeled by storeName and keyed by storeId -- so the sheet lists all stores, including
    // ones with no unread items, matching the legacy notification screen. The feed carries platform
    // store ids (per-store reads above), so a row's storeId scopes the list exactly. storeIds stays
    // an array for the sheet's search-by-id, here always the single store id. GLOBAL is never a row.
    const storeOptions = useMemo<IStoreFilterOption[]>(() => reduxStoreList
        .filter((s) => s.storeId && s.storeId !== STORE_AGNOSTIC_STORE_ID)
        .map((s): IStoreFilterOption => ({ storeId: s.storeId, storeName: s.storeName || s.storeId, storeIds: [s.storeId] }))
        .sort((a, b) => a.storeName.localeCompare(b.storeName)),
    [reduxStoreList])

    // The store filter only scopes the multi-store "All stores" list; "Current store" is one store.
    // Scope is pinned to the actual underlying store ids (see applyStoreFilter), so a collapsed
    // same-name store stays scoped as long as ANY of its ids is still in the feed -- it never hangs
    // off a single representative id that can change with feed order or vanish on acknowledge (B1).
    const allScoped = useMemo(
        () => (selectedStoreIds.length === 0
            ? all
            : all.filter((n) => !!n.storeId && selectedStoreIds.includes(n.storeId))),
        [all, selectedStoreIds],
    )

    // Rows currently selected, as representative ids: a row counts as selected when any of its
    // underlying ids is in the applied scope. Drives the sheet's checked state, the filled filter
    // icon and the summary -- and so hides them for the one frame an emptied feed has no options yet.
    const selectedRepIds = useMemo(
        () => storeOptions.filter((o) => o.storeIds.some((id) => selectedStoreIds.includes(id))).map((o) => o.storeId),
        [storeOptions, selectedStoreIds],
    )
    const visible = activeTab === 'current-store' ? current : allScoped
    const truncated = activeTab === 'current-store' ? storeTruncated : allTruncated
    // Only the store read failed: the tab shows the error, not an "all caught up" that is not true.
    const tabUnavailable = activeTab === 'current-store' && storeLoadFailed
    // "All stores" is view only: producer actions are only offered on the Current store tab.
    const showActions = activeTab === 'current-store'
    const immediate = useMemo(() => visible.filter(needsImmediateAttention), [visible])
    const immediateUnreadCount = useMemo(() => countUnread(immediate), [immediate])
    const categoryGroups = useMemo(() => groupByCategory(visible.filter((n) => !needsImmediateAttention(n))), [visible])
    const currentStoreUnreadCount = useMemo(() => countUnread(current), [current])

    // Keep selections valid against the authorized store list: the filter lists every store, so a
    // user can scope to one with zero unread items (it shows empty, like legacy). Only a store the
    // user loses access to is dropped from the scope.
    useEffect(() => {
        const authorized = new Set(reduxStoreList.map((s) => s.storeId))
        setSelectedStoreIds((prev) => {
            const pruned = prev.filter((id) => authorized.has(id))
            return pruned.length === prev.length ? prev : pruned
        })
    }, [reduxStoreList])

    // The sheet toggles rows by their representative id; translate those to the full set of
    // underlying ids, so the applied scope is the real feed ids and survives representative changes.
    const applyStoreFilter = useCallback((appliedRepIds: string[]) => {
        setSelectedStoreIds(storeOptions.filter((o) => appliedRepIds.includes(o.storeId)).flatMap((o) => o.storeIds))
    }, [storeOptions])

    const openDetail = useCallback((n: IActionCenterNotification) => {
        setView((from) => ({ kind: 'detail', notificationId: n.notificationId, from }))
    }, [])

    // READ drops the row from the unread feed but keeps it in the store's history.
    const acknowledge = useCallback(async (n: IActionCenterNotification) => {
        await ActionCenterAPIService.acknowledge(n.notificationId)
        setAllUnread((prev) => (prev ?? []).filter((item) => item.notificationId !== n.notificationId))
        setStoreHistory((prev) => prev.map((item) => (item.notificationId === n.notificationId ? { ...item, status: 'READ' } : item)))
        setView((v) => (v.kind === 'detail' ? v.from : v))
    }, [])

    const onTabChange = (tab: ActionCenterTab) => {
        setActiveTab(tab)
        setView({ kind: 'list' })
    }

    // The store history carries the up-to-date status for rows that appear in both lists.
    const selected = view.kind === 'detail'
        ? current.find((n) => n.notificationId === view.notificationId) ?? all.find((n) => n.notificationId === view.notificationId)
        : undefined

    // The row aged out underneath the detail view: fall back to where we came from.
    useEffect(() => {
        if (view.kind === 'detail' && allUnread !== null && !selected) setView(view.from)
    }, [view, selected, allUnread])

    if (isLoading) {
        return (
            <Column alignmentHorizontal="center" spacingInset="600 400" data-testid="action-center-loading">
                <Loader />
                <Text>{t(bundle, LOADING)}</Text>
            </Column>
        )
    }
    if (loadFailed) {
        return (
            <Column alignmentHorizontal="center" spacingInset="400">
                <Box type="outline" width="100%" spacingInset="300 450">
                    <Text type="b300" color="error" data-testid="action-center-error">{t(bundle, LOAD_ERROR)}</Text>
                </Box>
            </Column>
        )
    }
    if (allUnread === null) return null

    if (view.kind === 'detail') {
        if (!selected) return null
        return (
            <div key={viewKey(view)} className="action-center" data-testid="action-center">
                <NotificationDetailView
                    notification={selected}
                    currentStoreId={currentStoreId}
                    showActions={showActions}
                    onBack={() => setView(view.from)}
                    onAcknowledge={acknowledge}
                />
            </div>
        )
    }

    if (view.kind === 'category') {
        const group = categoryGroups.find((g) => g.category === view.category)
        return (
            <div key={viewKey(view)} className="action-center" data-testid="action-center">
                <BackHeader onBack={() => setView({ kind: 'list' })} title={categoryLabel(view.category)} />
                <Column spacing="none">
                    {(group?.notifications ?? []).map((n) => (
                        <NotificationCard key={n.notificationId} notification={n} currentStoreId={currentStoreId} showActions={showActions} onOpen={openDetail} />
                    ))}
                    {!group && <EmptyState />}
                </Column>
            </div>
        )
    }

    return (
        <div key={viewKey(view)} className="action-center" data-testid="action-center">
            <Column spacing="300" spacingInset="300 400 none 400">
                <StoreTabs
                    activeTab={activeTab}
                    allStoresCount={all.length}
                    currentStoreCount={currentStoreUnreadCount}
                    hasCurrentStore={hasCurrentStore}
                    onChange={onTabChange}
                    canFilter={activeTab === 'all-stores' && storeOptions.length > 0}
                    filterActive={selectedRepIds.length > 0}
                    filterOpen={isFilterOpen}
                    onFilterTap={() => setFilterOpen(true)}
                />
                {activeTab === 'all-stores' && selectedRepIds.length > 0 && (
                    <Row width="100%" alignmentVertical="center" widths={['fill', 'fit']} data-testid="filter-summary">
                        <Text type="b200" color="secondary">
                            {t(bundle, STORE_SELECTION_SUMMARY, { selectedCount: selectedRepIds.length, totalCount: storeOptions.length })}
                        </Text>
                        <Button type="link" size="small" onClick={() => setSelectedStoreIds([])} data-testid="clear-filter-button">
                            {t(bundle, CLEAR_FILTER)}
                        </Button>
                    </Row>
                )}
                {activeTab === 'all-stores' && <ViewOnlyNotice />}
                {tabUnavailable && <StoreLoadErrorNotice />}
                {truncated && <TruncatedNotice />}
            </Column>
            {!tabUnavailable && visible.length === 0 && <EmptyState />}
            {immediate.length > 0 && (
                <>
                    <SectionHeader title={t(bundle, NEEDS_IMMEDIATE_ATTENTION)} count={immediateUnreadCount} />
                    <Column spacing="none" data-testid="immediate-attention">
                        {immediate.map((n) => (
                            <NotificationCard key={n.notificationId} notification={n} currentStoreId={currentStoreId} showActions={showActions} onOpen={openDetail} />
                        ))}
                    </Column>
                </>
            )}
            <CategoryAccordion groups={categoryGroups} onSelect={(category) => setView({ kind: 'category', category })} />
            <StoreFilterSheet
                open={isFilterOpen}
                onClose={() => setFilterOpen(false)}
                stores={storeOptions}
                initialSelectedStoreIds={selectedRepIds}
                onApply={applyStoreFilter}
            />
        </div>
    )
}

const EmptyState: React.FC = () => {
    const [bundle] = useBundle(BUNDLE_NAME)
    return (
        <Column alignmentHorizontal="center" spacingInset="600 400" data-testid="action-center-empty">
            <Text type="b300" color="secondary">{t(bundle, EMPTY_ALL_CAUGHT_UP)}</Text>
        </Column>
    )
}

/**
 * Key for the `.action-center` scroll container, distinct per view. The list, category and detail
 * views are swapped in place (not routed), and all three render the same root element at the same
 * tree position, so without a key React reuses one DOM node and the browser keeps its `scrollTop`
 * across the swap. Opening a category after scrolling down the list then lands mid-way through the
 * category, or at its bottom once the browser clamps the stale offset to the shorter content. A
 * key that changes with the view mounts a fresh node, which starts at the top.
 */
const viewKey = (view: ActionCenterView): string => {
    switch (view.kind) {
    case 'category': return `category:${view.category}`
    case 'detail': return `detail:${view.notificationId}`
    default: return 'list'
    }
}

export default ActionCenterPage
